// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- a verbatim copy of JavaScript, see below
// @ts-nocheck — the span below is the `.cjs` JavaScript verbatim; typing it would make it a port, not a copy.
/**
 * KB-4516 — the VENDORED TypeScript copy of `scripts/camp-data/_shared/sessionWindowRule.cjs`, the ONE
 * definition of the default drop-off/pick-up window (KB-3613). It exists so the two TypeScript writers reach
 * the rule: `prepareOfferDataCore.ts` (the admin / server offer build, bundled into the dashboard through the
 * `@/functions-shared` Vite alias) and the dashboard's `proposalToDraftVariation`. Neither can import the
 * pipeline's CommonJS file — the client bundle cannot load `module.exports` (the same reason as
 * `regUrlValidation.ts` and `nonPageEndpointUrl.ts`).
 *
 * ⛔ NOT a hand-written port. Everything from `const SESSION_WINDOW_DEFAULTS` to the end of this file is the
 * `.cjs` source byte-for-byte, with its one `module.exports = {…};` line spelled `export {…};`.
 * `__tests__/sessionWindowRule.vendored.test.ts` fails on any other difference. To change the rule, edit the
 * `.cjs`, then re-copy that span here.
 */

const SESSION_WINDOW_DEFAULTS = Object.freeze({
  dropOff: Object.freeze({
    label: "drop-off",
    from: "regularProgrammingStart",
    keys: Object.freeze(["regularDropOffStart", "regularDropOffEnd", "dropOffStart", "dropOffEnd"]),
  }),
  pickUp: Object.freeze({
    label: "pick-up",
    from: "regularProgrammingEnd",
    keys: Object.freeze(["regularPickUpStart", "regularPickUpEnd", "pickUpStart", "pickUpEnd"]),
  }),
});

const isUnset = (v) => v === undefined || v === null;

/**
 * The window keys to ADD to `timings`. A group (drop-off, pick-up) is defaulted from its programming
 * bound only when NONE of its four keys is set; a group with any key set is somebody's window — a
 * curator's, or the camp's real drop-off time — and is left exactly as it is, so a default can never
 * sit beside a real value and disagree with it. "Set" is anything but null/undefined: the dashboard
 * stores an unset window as null. Empty unless both programming bounds are finite.
 */
function sessionWindowFill(timings) {
  const t = timings && typeof timings === "object" ? timings : {};
  if (!Number.isFinite(t.regularProgrammingStart) || !Number.isFinite(t.regularProgrammingEnd)) return {};
  const fill = {};
  for (const { from, keys } of Object.values(SESSION_WINDOW_DEFAULTS)) {
    if (!keys.every((k) => isUnset(t[k]))) continue;
    for (const k of keys) fill[k] = t[from];
  }
  return fill;
}

/**
 * KB-4533 — the carry-forward, the ONE rule every writer that puts a computed `variationData` onto an EXISTING
 * offer applies. The offer compute defaults the window the catalog lacks (`sessionWindowFill`); on an existing
 * offer that default is not the catalog's voice, so each key the compute filled goes back to the catalog's own
 * value (absent, or null) — per GROUP, as the fill is: a group is kept only where the STORED offer already holds
 * every one of its keys at the filled value (an offer created with the default keeps it). A stored window that
 * matches the default on some keys only (a curated drop-off ending at the programming start) reverts the whole
 * group, so the result is never half a window. Returns `variationData` unchanged (same object) when nothing reverts.
 */
function withoutDefaultedWindow(variationData, { catalogTimings, storedTimings } = {}) {
  const vd = variationData;
  if (!vd || typeof vd !== "object" || !vd.timings || typeof vd.timings !== "object") return vd;
  const filled = sessionWindowFill(catalogTimings);
  const stored = storedTimings && typeof storedTimings === "object" ? storedTimings : {};
  const kept = new Set();
  for (const { keys } of Object.values(SESSION_WINDOW_DEFAULTS)) {
    if (keys.every((k) => Object.prototype.hasOwnProperty.call(filled, k) && stored[k] === filled[k])) for (const k of keys) kept.add(k);
  }
  const reverted = Object.keys(filled).filter((k) => vd.timings[k] === filled[k] && !kept.has(k));
  if (reverted.length === 0) return vd;
  const t = { ...vd.timings };
  for (const k of reverted) {
    if (catalogTimings && Object.prototype.hasOwnProperty.call(catalogTimings, k)) t[k] = catalogTimings[k];
    else delete t[k];
  }
  return { ...vd, timings: t };
}

/**
 * KB-4535 — on an UPDATE, the stated window never reaches the stored offer through the whole-field `variationData`
 * diff, and never changes what that diff records. This is the diff's whole window rule: it replaces the bare
 * `withoutDefaultedWindow` call when a window is stated (and is exactly that call when none is).
 *
 * Per stated GROUP, whole or not at all:
 *   - the STORED offer already holds every key of the group at the stated value (an offer created with it, or one
 *     whose proposal was approved): the stored keys stand. Without this, night 2 proposes away the window night 1
 *     wrote (the KB-4533 class).
 *   - otherwise the group diffs exactly as it would with nothing stated: the catalog's own keys, the default filled
 *     where the catalog has none, then `withoutDefaultedWindow`. `moveOfferVariation` applies this same rule to
 *     the offer's `observedWindow` stamp (KB-4552), so the nightly's recorded `to`, a person's rejection of it, and
 *     an audited move all agree. The disagreement is `deriveSessionWindowChangeFlags`'s one compound proposal.
 *
 * Order matters for the first case: `withoutDefaultedWindow` reverts per KEY, and a stated drop-off END is the
 * programming start, which is also the default's value, so the agreeing groups are re-asserted after it.
 *
 * KB-4552 — moved here verbatim from `_reconcile/observedToOffersLib.cjs` (which still exports it under the same
 * name) so the vendored move tool can reach it (rulings 386a3a88 / d4e3ac54).
 *
 * KB-4552 (ruling 5778f19e) — `stampTimings` is the offer's `observedWindow` stamp as `timings` keys
 * (`observedWindowTimings`): what the vendor stated when the offer was created, or what an approve of the window
 * card last wrote. A stamped group the stored offer holds at the stamp value stands too, exactly as an agreeing
 * stated group does, so a vendor that later REVISES its statement does not turn the stored, once-stated group into a
 * whole-field card to the catalog's window (the revision is the compound proposal alone). The nightly passes the
 * stamp and the current statement; the move tool, which never sees the statement, passes the stamp, so both reach
 * the same value. With neither stated nor stamped it is exactly `withoutDefaultedWindow`.
 */
function withoutStatedWindow(variationData, { statedTimings, storedTimings, catalogTimings, stampTimings } = {}) {
  const out = withoutStatedWindowOnly(variationData, { statedTimings, storedTimings, catalogTimings });
  // KB-4552 (ruling 5778f19e) — the stamp only KEEPS: a stamped group the stored offer holds at the stamp value is
  // laid over the result; a stamped group it does not hold changes nothing.
  const stamp = stampTimings && typeof stampTimings === "object" ? stampTimings : {};
  if (!out || typeof out !== "object" || !out.timings || typeof out.timings !== "object" || Object.keys(stamp).length === 0) return out;
  const stored = storedTimings && typeof storedTimings === "object" ? storedTimings : {};
  const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
  const held = Object.values(SESSION_WINDOW_DEFAULTS).filter(({ keys }) => keys.every((k) => has(stamp, k) && has(stored, k) && stored[k] === stamp[k]));
  const changed = held.some(({ keys }) => keys.some((k) => !has(out.timings, k) || out.timings[k] !== stored[k]));
  if (!changed) return out;
  const t = { ...out.timings };
  for (const { keys } of held) for (const k of keys) t[k] = stored[k];
  return { ...out, timings: t };
}

/** `withoutStatedWindow` before the stamp (KB-4535's rule, moved verbatim by KB-4552): the statement alone. */
function withoutStatedWindowOnly(variationData, { statedTimings, storedTimings, catalogTimings } = {}) {
  const vd = variationData;
  if (!vd || typeof vd !== "object" || !vd.timings || typeof vd.timings !== "object") return vd;
  const stated = statedTimings && typeof statedTimings === "object" ? statedTimings : {};
  if (Object.keys(stated).length === 0) return withoutDefaultedWindow(vd, { catalogTimings, storedTimings });
  const stored = storedTimings && typeof storedTimings === "object" ? storedTimings : {};
  const catalog = catalogTimings && typeof catalogTimings === "object" ? catalogTimings : {};
  const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
  const groups = Object.values(SESSION_WINDOW_DEFAULTS).filter(({ keys }) => keys.some((k) => has(stated, k)));
  const agreeing = groups.filter(({ keys }) => keys.every((k) => has(stated, k) && has(stored, k) && stored[k] === stated[k]));
  // The payload as offer-compute builds it with nothing stated: each stated group back to the catalog's keys...
  const unstated = { ...vd.timings };
  for (const { keys } of groups) {
    for (const k of keys) {
      if (has(catalog, k)) unstated[k] = catalog[k];
      else delete unstated[k];
    }
  }
  // ...then the default for a group that is still unset (offer-compute's own sessionWindowFill step).
  const out = withoutDefaultedWindow({ ...vd, timings: { ...unstated, ...sessionWindowFill(unstated) } }, { catalogTimings, storedTimings });
  const t = { ...out.timings };
  for (const { keys } of agreeing) for (const k of keys) t[k] = stored[k];
  const same = (a, b) => Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((k) => has(b, k) && a[k] === b[k]);
  return same(t, vd.timings) ? vd : { ...out, timings: t };
}

/**
 * KB-4552 — an offer's `observedWindow` stamp (`{ dropOffStartMinutes, dropOffEndMinutes, pickUpStartMinutes,
 * pickUpEndMinutes }`, `offerWriteCore.observedWindowOfRow`) as the `timings` keys it stands for: per GROUP, whole or
 * not at all, the group's start keys at `<group>StartMinutes` and its end keys at `<group>EndMinutes`, when both are
 * whole minutes inside one day and start <= end — the rule `offerComputeCore.statedWindowTimings` applied to the row
 * the stamp was taken from (pinned equal to it by test). Empty for an unstamped offer. The ONE mapping the nightly and
 * the move tool both use.
 */
function observedWindowTimings(stamp) {
  const w = stamp && typeof stamp === "object" ? stamp : {};
  const minute = (v) => (Number.isInteger(v) && v >= 0 && v <= 24 * 60 ? v : null);
  const out = {};
  for (const [name, { keys }] of Object.entries(SESSION_WINDOW_DEFAULTS)) {
    const start = minute(w[`${name}StartMinutes`]);
    const end = minute(w[`${name}EndMinutes`]);
    if (start == null || end == null || start > end) continue;
    for (const k of keys) out[k] = k.endsWith("Start") ? start : end;
  }
  return out;
}

export { SESSION_WINDOW_DEFAULTS, sessionWindowFill, withoutDefaultedWindow, withoutStatedWindow, observedWindowTimings };
